import { AtlassianClient } from '@canvas/atlassian-client';
import { retryWithBackoff } from '@canvas/retry';
import { sleep } from '@canvas/sleep';

import { DatasourceService } from './datasource-service';

type JiraServer = {
  id: string;
  url: string;
};

type LoadingState<T> = {
  status: 'initial' | 'loading' | 'success' | 'fail';
  data?: T;
};

const fetcher = async (input: RequestInfo, init?: RequestInit) =>
  await retryWithBackoff({
    callback: () => globalThis.fetch(input, init),
    maxRetries: 3,
  });

export const jiraServerUrl = '/wiki/rest/jiraanywhere/1.0/servers';
const sleepTime = 50;

class DatasourceCloudIdMigrationService {
  private serverIdToCloudIdMap: LoadingState<Map<string, string | undefined>>;
  private readonly client = new AtlassianClient(fetcher);

  private constructor() {
    this.serverIdToCloudIdMap = { status: 'initial' };
  }

  private async loadServerIdToCloudIdMap() {
    this.serverIdToCloudIdMap = { status: 'loading' };
    try {
      const [jiraServers, jiraDatasourceSites] = await Promise.all([
        this.client.get<JiraServer[]>(globalThis.location.origin + jiraServerUrl),
        new DatasourceService('jira').getAvailableSites(),
      ]);
      const urlToCloudIdMap = new Map(
        jiraDatasourceSites.map((site) => [
          site.url.toString().slice(0, -1), // trim the trailing '/' in 'https://hello.atlassian.net/'
          site.cloudId,
        ]),
      );
      const data = new Map(
        jiraServers.map((server) => [server.id, urlToCloudIdMap.get(server.url)]),
      );
      this.serverIdToCloudIdMap = {
        status: 'success',
        data,
      };
    } catch {
      this.serverIdToCloudIdMap = { status: 'fail' };
      return;
    }
  }

  private async loadDataIfNeeded() {
    if (this.serverIdToCloudIdMap.status === 'initial') {
      await this.loadServerIdToCloudIdMap();
    }

    while (this.serverIdToCloudIdMap.status === 'loading') {
      await sleep(sleepTime);
    }
  }

  public async loadServerIdToCloudId(serverId: string) {
    await this.loadDataIfNeeded();
    return this.serverIdToCloudIdMap.data?.get(serverId);
  }

  public async loadCloudIdToServerId(cloudId: string) {
    await this.loadDataIfNeeded();

    const serverIdToCloudIdData = this.serverIdToCloudIdMap.data;
    if (!serverIdToCloudIdData) {
      return;
    }
    return [...serverIdToCloudIdData.entries()].find(
      ([, entryCloudId]) => entryCloudId === cloudId,
    )?.[0];
  }

  private static instance: DatasourceCloudIdMigrationService | null = null;

  static getInstance = (): DatasourceCloudIdMigrationService => {
    if (!DatasourceCloudIdMigrationService.instance) {
      DatasourceCloudIdMigrationService.instance =
        new DatasourceCloudIdMigrationService();
    }

    return DatasourceCloudIdMigrationService.instance;
  };

  static reset = () => {
    DatasourceCloudIdMigrationService.instance = null;
  };
}

export async function getCloudIdFromServerId(serverId: string) {
  return await DatasourceCloudIdMigrationService.getInstance().loadServerIdToCloudId(
    serverId,
  );
}

export async function getServerIdFromCloudId(cloudId: string) {
  return await DatasourceCloudIdMigrationService.getInstance().loadCloudIdToServerId(
    cloudId,
  );
}

export function resetDatasourceCloudIdMigrationService() {
  return DatasourceCloudIdMigrationService.reset();
}
